<div class="page-head">
  <div>
    <h1 class="title">Applications</h1>
    <p class="desc">
      Apps from AGW API partners that can access your airline content. Request or revoke access at
      any time.
    </p>
  </div>
</div>

@if (canManage) {
  <div class="tabs mb-4" role="tablist">
    @for (tab of tabs; track tab.key) {
      <button
        type="button"
        role="tab"
        class="tab"
        [class.on]="activeTab === tab.key"
        [attr.aria-selected]="activeTab === tab.key"
        (click)="setTab(tab.key)"
      >
        {{ tab.label }}
        <span class="badge badge-secondary">{{ appsFor(tab.key).length }}</span>
      </button>
    }
  </div>
}

@if (!canManage) {
  <div class="card flex min-h-[160px] items-center justify-center border-dashed shadow-none">
    <p class="m-0 p-6 text-center text-sm text-muted-foreground">
      Applications are managed by the managers of your main agency.
    </p>
  </div>
} @else if (!loaded) {
  <div class="flex flex-col gap-3">
    @for (i of [1, 2, 3]; track i) {
      <div class="card flex items-center gap-4 p-5">
        <div class="skeleton h-11 w-11 flex-none"></div>
        <div class="flex flex-1 flex-col gap-2">
          <div class="skeleton h-4 w-1/4"></div>
          <div class="skeleton h-3 w-1/2"></div>
        </div>
      </div>
    }
  </div>
} @else {
  @if (activeTab === 'available') {
    <!-- Private apps are never listed, so they are requested by the ID their developer shares. -->
    <form class="card mb-4 p-5" (ngSubmit)="requestPrivateApp()">
      <div class="flex flex-wrap items-end gap-3">
        <div class="field min-w-0 flex-1">
          <label class="label" for="request-app-id">Request a private application</label>
          <input
            id="request-app-id"
            name="requestId"
            class="input"
            autocomplete="off"
            placeholder="App ID"
            [class.err]="!!requestError"
            [attr.aria-invalid]="!!requestError"
            [(ngModel)]="requestId"
            (ngModelChange)="requestError = ''"
          />
        </div>
        <button type="submit" class="btn btn-primary" [disabled]="requesting || !requestId.trim()">
          {{ requesting ? 'Requesting…' : 'Request access' }}
        </button>
      </div>
      @if (requestError) {
        <p class="error mt-2">{{ requestError }}</p>
      } @else {
        <p class="hint mt-2">
          Private applications aren’t listed below. Enter the App ID their developer shared with
          you.
        </p>
      }
    </form>
  }
  @if (activeTab === 'suspended') {
    <p class="hint mb-4">
      These applications’ developers have suspended your access. Your API keys are kept and work
      again once the developer resumes it.
    </p>
  }
  @let apps = appsFor(activeTab);
  @if (!apps.length) {
    <div class="card flex min-h-[160px] items-center justify-center border-dashed shadow-none">
      <p class="m-0 p-6 text-center text-sm text-muted-foreground">
        @switch (activeTab) {
          @case ('active') {
            No active applications yet. Browse the Available tab to request one.
          }
          @case ('available') {
            No applications available right now.
          }
          @default {
            Nothing here.
          }
        }
      </p>
    </div>
  } @else {
    <div class="flex flex-col gap-3">
      @for (app of apps; track app.id) {
        @let permissions = app.permissions | filterPermissions;
        <article class="card app-tile">
          <a
            class="app-tile-link"
            [routerLink]="['/applications', app.id]"
            [attr.aria-label]="'View ' + app.name"
          >
            <div class="logo h-11 w-11 text-sm" [class.logo-muted]="!app.logo_url">
              @if (app.logo_url) {
                <img [src]="app.logo_url" alt="" />
              } @else {
                {{ initials(app.name) }}
              }
            </div>
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <h3 class="m-0 truncate text-base font-semibold text-foreground">
                {{ app.name }}
              </h3>
              @if (app.description) {
                <p class="m-0 line-clamp-1 text-sm text-muted-foreground">
                  {{ app.description }}
                </p>
              }
            </div>
            <div class="app-tile-perms">
              @for (permission of permissions.slice(0, 4); track permission) {
                <span class="badge badge-secondary">{{ permission }}</span>
              }
              @if (permissions.length > 4) {
                <span class="badge badge-outline">+{{ permissions.length - 4 }}</span>
              }
            </div>
          </a>
          <div class="app-tile-actions">
            @if (!app.connection) {
              <button
                type="button"
                class="btn btn-primary btn-sm"
                (click)="openConfirmationModal(app)"
              >
                Request access
              </button>
            } @else if (canDrop(app)) {
              <button
                type="button"
                class="btn btn-outline btn-sm"
                (click)="openConfirmationModal(app)"
              >
                {{ toggleLabel(app) }}
              </button>
            }
            <a
              class="btn btn-ghost btn-icon btn-sm text-muted-foreground"
              [routerLink]="['/applications', app.id]"
              [attr.aria-label]="'Open ' + app.name"
            >
              <app-icon name="chevron-right" />
            </a>
          </div>
        </article>
      }
    </div>
  }
}

<app-confirm-dialog
  [open]="modalVisible"
  [title]="confirmation.title"
  [confirmLabel]="selectedApp?.connection ? toggleLabel(selectedApp!) : 'Request access'"
  [destructive]="!!selectedApp?.connection"
  [busy]="saving"
  (confirm)="confirmToggle()"
  (cancel)="closeModal()"
>
  <p class="m-0">{{ confirmation.message }}</p>
  @if (selectedApp && !selectedApp.connection) {
    @let requested = selectedApp.permissions | filterPermissions;
    @if (requested.length) {
      <div class="mt-4 flex flex-wrap gap-1.5">
        @for (permission of requested; track permission) {
          <span class="badge badge-secondary">{{ permission }}</span>
        }
      </div>
    }
  }
</app-confirm-dialog>
